import React, { useEffect, useState } from 'react';
import Loader from '../../Shared/Loader';

import '../../../css/CouponCodeList.css';
import * as XLSX from 'xlsx';
import AddCouponModal from './AddCouponModal';
import ViewCouponModal from './ViewCouponModal';
import EditCouponModal from './EditCouponModal';
import api from '../../../api/axiosConfig';

interface PromoCode {
  promo_code_id: string;
  code: string;
  code_type: string;
  fee_discount: number;
  applicable_center_id?: string;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  redemption_limit?: number;
  current_redemption_count?: number;
  created_at: string;
  disabled_at: string;
}

interface Center {
  _id: string;
  name: string;
}

const CouponCodeList: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<PromoCode | null>(null);
  const [coupons, setCoupons] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [centersMap, setCentersMap] = useState<Record<string, string>>({});

  // ✅ Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  useEffect(() => {
    fetchCoupons();
    fetchCenters();
  }, []);

  const fetchCoupons = async () => {
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) {
        console.error("Authentication token not found.");
        setLoading(false);
        return;
      }

      const res = await api.payment_api.get<PromoCode[]>('/admin/promocodes', {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCoupons(res.data);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    } finally {
      setLoading(false);
    }
  };

const fetchCenters = async () => {
  try {
    const token = sessionStorage.getItem("accessToken");
    if (!token) return;

    const res = await api.center_api.get("/api/v1/centers", {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.data?.success && Array.isArray(res.data.data)) {
      const map: Record<string, string> = {};

      res.data.data.forEach((center: Center) => {
        map[center._id] = center.name;
      });

      setCentersMap(map);
    }
  } catch (error) {
    console.error("Error fetching centers:", error);
  }
};



  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN');
  };

  const filterCouponsByDate = (coupons: PromoCode[], filter: string | null) => {
    if (!filter) return coupons;
    const today = new Date();
    let startDate = new Date();
    switch (filter) {
      case 'last2days':
        startDate.setDate(today.getDate() - 2);
        break;
      case 'last7days':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'last30days':
        startDate.setDate(today.getDate() - 30);
        break;
      default:
        return coupons;
    }
    return coupons.filter(coupon => {
      const createdDate = new Date(coupon.created_at);
      return createdDate >= startDate && createdDate <= today;
    });
  };

  // ✅ Apply search + date filter
  const filteredCoupons = filterCouponsByDate(
    coupons.filter((coupon) =>
      coupon.code.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    selectedFilter
  );

  // ✅ Pagination Logic
  const totalPages = Math.ceil(filteredCoupons.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedCoupons = filteredCoupons.slice(startIndex, startIndex + recordsPerPage);

  const handleRowClick = (coupon: PromoCode) => {
    setSelectedCoupon(coupon);
    setShowViewModal(true);
  };

  const handleExportToExcel = () => {
    const dataToExport = filteredCoupons.map((coupon, idx) => {
      const isExpired = new Date(coupon.valid_until) < new Date();
      const redemptionInfo = coupon.redemption_limit
        ? `${coupon.current_redemption_count || 0}/${coupon.redemption_limit}`
        : `${coupon.current_redemption_count || 0}/∞`;

      return {
        'No.': idx + 1,
        'Code': coupon.code,
        'Coupon Type': coupon.code_type,
        'Fee Discount': `₹${coupon.fee_discount || 0}`,
        'Venues': coupon.applicable_center_id ? 'Specific Center' : 'All Centers',
        'Validity': `${formatDate(coupon.valid_from)} - ${formatDate(coupon.valid_until)}`,
        'Status': isExpired || !coupon.is_active ? 'Expired' : 'Active',
        'Redemptions': redemptionInfo,
        'Created On': formatDate(coupon.created_at),
        'Disabled On': coupon.disabled_at ? formatDate(coupon.disabled_at) : formatDate(coupon.disabled_at)
      };
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    XLSX.utils.book_append_sheet(wb, ws, 'Coupon Codes');
    XLSX.writeFile(wb, 'coupon_codes.xlsx');
  };

  return (
    <div className='main-container'>
      <div className="coupon-header">
        <h2>List Of Coupon Codes</h2>
        <button className="add-button" onClick={() => setShowModal(true)}>
          + Add New Coupon Code
        </button>
      </div>

      <div className="coupon-container">
        {/* Search + Filter + Export */}
        <div className="coupon-controls">
          <input
            type="text"
            placeholder="Search promo codes"
            className="search-box"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page when searching
            }}
          />

          <div className='filter-export-div'>
            <div className="filter-container">
              <button
                className="filter-button"
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#131718ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16l-6 8v6l-4 2v-8L4 4z" />
                </svg>
                Filter
                {selectedFilter && <span className="active-filter-dot"></span>}
              </button>

              {showFilterDropdown && (
                <div className="filter-dropdown">
                  {['last2days', 'last7days', 'last30days'].map((f) => (
                    <div
                      key={f}
                      className={`filter-option ${selectedFilter === f ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedFilter(f);
                        setShowFilterDropdown(false);
                        setCurrentPage(1);
                      }}
                    >
                      {f === 'last2days' ? 'Last 2 days' : f === 'last7days' ? 'Last 7 days' : 'Last 30 days'}
                    </div>
                  ))}
                  {selectedFilter && (
                    <div
                      className="filter-option clear"
                      onClick={() => {
                        setSelectedFilter(null);
                        setShowFilterDropdown(false);
                      }}
                    >
                      Clear filter
                    </div>
                  )}
                </div>
              )}
            </div>

            <button className="export-button" onClick={handleExportToExcel}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#312c2cff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="2" ry="2" />
                <rect x="14" y="3" width="7" height="7" rx="2" ry="2" />
                <rect x="14" y="14" width="7" height="7" rx="2" ry="2" />
                <rect x="3" y="14" width="7" height="7" rx="2" ry="2" />
              </svg>
              Export
            </button>
          </div>
        </div>

        {/* Table */}
        <table className="coupon-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Code</th>
              <th>Coupon Type</th>
              <th>Fee Discount</th>
              <th>Venues</th>
              <th>Validity</th>
              <th>Status</th>
              <th>Redemptions</th>
              <th>Created On</th>
              <th>Disabled On</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10}><Loader inline text="Loading Coupons..." /></td></tr>
            ) : paginatedCoupons.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center' }}>No coupon codes found.</td></tr>
            ) : (
              paginatedCoupons.map((coupon, idx) => {
                const isExpired = new Date(coupon.valid_until) < new Date();
                const redemptionInfo = coupon.redemption_limit
                  ? `${coupon.current_redemption_count || 0}/${coupon.redemption_limit}`
                  : `${coupon.current_redemption_count || 0}/∞`;
                return (
                  <tr key={coupon.promo_code_id} onClick={() => handleRowClick(coupon)} style={{ cursor: 'pointer' }}>
                    <td>{startIndex + idx + 1}</td>
                    <td>{coupon.code}</td>
                    <td>
                      {coupon.code_type?.toLowerCase() === 'promo' && <span className="badge purple">Promo</span>}
                      {coupon.code_type?.toLowerCase() === 'venue' && <span className="badge blue">Venue</span>}
                    </td>
                    <td>₹{coupon.fee_discount || 0}</td>
                    <td>
  {coupon.applicable_center_id
    ? centersMap[coupon.applicable_center_id] || "Unknown Center"
    : "All Centers"}
</td>

                    <td>{formatDate(coupon.valid_from)} - {formatDate(coupon.valid_until)}</td>
                    <td>
                      <span className={`status-badge ${isExpired || !coupon.is_active ? 'expired' : 'active'}`}>
                        {isExpired || !coupon.is_active ? 'Expired' : 'Active'}
                      </span>
                    </td>
                    <td>{redemptionInfo}</td>
                    <td>{formatDate(coupon.created_at)}</td>
                    <td>{coupon.disabled_at ? formatDate(coupon.disabled_at) : formatDate(coupon.disabled_at)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* ✅ Pagination */}
        <div className="pagination">
          <span>
            Showing {paginatedCoupons.length > 0 ? `${startIndex + 1}-${startIndex + paginatedCoupons.length}` : '0'} of {filteredCoupons.length} promo codes
          </span>
          <div className="pagination-buttons">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </button>

            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={currentPage === i + 1 ? 'active' : ''}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        </div>

        {/* Modals */}
        {showModal && (
          <AddCouponModal
            onClose={() => {
              setShowModal(false);
              fetchCoupons();
            }}
          />
        )}

        {showViewModal && selectedCoupon && (
          <ViewCouponModal
            coupon={selectedCoupon}
            onClose={() => setShowViewModal(false)}
            onUpdate={fetchCoupons}
            onEdit={() => {
              setShowViewModal(false);
              setShowEditModal(true);
            }}
          />
        )}

        {showEditModal && selectedCoupon && (
          <EditCouponModal
            coupon={selectedCoupon}
            onClose={() => setShowEditModal(false)}
            onUpdate={() => {
              fetchCoupons();
              setShowEditModal(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CouponCodeList;
